import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Button, Card, Row, Col, Input, Table, Segmented, ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import { HappyProvider } from "@ant-design/happy-work-theme";
import { SearchOutlined } from "@ant-design/icons";
import { DataBadge, AppPagination } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";

import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";

import { actionAutoridadesAllGet } from "../../redux/actions/autoridades/autoridades";
import { PAGE_SIZE } from "./materias/laboral/laboralProcedimiento.config.js";

dayjs.locale("es");

const collapseSpaces = (str) => String(str || "").replace(/\s+/g, " ").trim();

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatDateShortEs = (d) => {
  if (!d) return "N/A";
  const x = dayjs(d);
  if (!x.isValid()) return String(d);
  return x.format("DD MMM YYYY");
};

const mapAutoridadToRow = (a) => {
  const isActive = Number(a?.active) === 1;
  return {
    id: a?.id,
    nombre: toTitleCase(a?.nombre),
    ciudad: a?.nombre_ciudad || "—",
    estado: a?.nombre_estado || "—",
    tipo: a?.nombre_tipo_autoridad || "—",
    estatus: isActive ? { label: "ACTIVA", kind: "primary" } : { label: "INACTIVA", kind: "gray" },
    fechaAlta: formatDateShortEs(a?.created_at),
  };
};

const STATUS_PILL_HEX = { primary: "#0A539A", gray: "#6B7280" };

function StatusPillAutoridad({ kind, label }) {
  return (
    <div style={{ width: 110, flexShrink: 0 }}>
      <DataBadge
        color={STATUS_PILL_HEX[kind] || kind}
        className="d-flex align-items-center justify-content-center gap-1 status-pill-badge"
        style={{ whiteSpace: "normal", width: "100%", boxSizing: "border-box", lineHeight: 1.3, textAlign: "center" }}
      >
        {label}
      </DataBadge>
    </div>
  );
}

const KPI_ICON_COLORS = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  secondary: { bg: "#F3F4F6", fg: "#6B7280" },
};

function KpiCardAutoridad({ iconSvg, tone, value, label, active = false, onClick }) {
  const colors = KPI_ICON_COLORS[tone] || KPI_ICON_COLORS.secondary;
  return (
    <Card
      role={onClick ? "button" : undefined}
      onClick={onClick}
      bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderColor: active ? "#0A539A" : undefined,
        borderWidth: active ? 2 : undefined,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 44, height: 44, background: colors.bg, color: colors.fg }}
      >
        {iconSvg}
      </span>
      <div>
        <div className="fs-4 fw-bold lh-1">{value}</div>
        <div className="small text-secondary">{label}</div>
      </div>
    </Card>
  );
}

function FooterPagerAutoridad({ total, page, setPage }) {
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <small className="text-secondary">
        Mostrando <strong>{start}</strong> a <strong>{end}</strong> de <strong>{total}</strong> resultados
      </small>
      <AppPagination current={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} size="sm" />
    </div>
  );
}

const ICONS = {
  check: (
    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z" />
  ),
  stop: (
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 208.1L222.1 255.1L175 303c-9.4 9.4-9.4 24.6 0 33.9c9.4 9.4 24.6 9.4 33.9 0l47-47l47 47c9.4 9.4 24.6 9.4 33.9 0c9.4-9.4 9.4-24.6 0-33.9l-47-47l47-47c9.4-9.4 9.4-24.6 0-33.9c-9.4-9.4-24.6-9.4-33.9 0l-47 47l-47-47c-9.4-9.4-24.6-9.4-33.9 0c-9.4 9.4-9.4 24.6 0 33.9z" />
  ),
  plus: (
    <path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z" />
  ),
  info: (
    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z" />
  ),
  pin: (
    <path d="M168.3 499.2C116.1 435 0 279.4 0 192C0 85.96 85.96 0 192 0C298 0 384 85.96 384 192C384 279.4 267 435 215.7 499.2C203.4 514.5 180.6 514.5 168.3 499.2H168.3zM192 256C227.3 256 256 227.3 256 192C256 156.7 227.3 128 192 128C156.7 128 128 156.7 128 192C128 227.3 156.7 256 192 256z" />
  ),
};

function TableView({ pageRows, total, page, setPage, navigate, canConsultar }) {
  const columns = [
    {
      title: "Nombre",
      key: "nombre",
      render: (_, r) => <div className="fw-semibold">{r.nombre}</div>,
    },
    {
      title: "Ciudad",
      key: "ciudad",
      render: (_, r) => <div className="small">{r.ciudad}</div>,
    },
    {
      title: "Estado",
      key: "estado",
      render: (_, r) => <div className="small">{r.estado}</div>,
    },
    {
      title: "Tipo de autoridad",
      key: "tipo",
      render: (_, r) => <div className="small">{r.tipo}</div>,
    },
    {
      title: "Estatus",
      key: "estatus",
      render: (_, r) => <StatusPillAutoridad kind={r.estatus.kind} label={r.estatus.label} />,
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "right",
      render: (_, r) => (
        <Button
          type="primary"
          block
          onClick={() => canConsultar && navigate(`/autoridades/${r.id}`)}
          disabled={!canConsultar}
          title={!canConsultar ? "Sin permiso de consulta" : undefined}
        >
          <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
            {ICONS.info}
          </svg>
          Ver detalles
        </Button>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Table columns={columns} dataSource={pageRows} rowKey={(r) => r.id} pagination={false} />
      <div className="p-3" style={{ borderTop: "1px solid #eef2f7", background: "#fff" }}>
        <FooterPagerAutoridad total={total} page={page} setPage={setPage} />
      </div>
    </Card>
  );
}

function CardsView({ rows, total, page, setPage, navigate, canConsultar }) {
  return (
    <>
      <Row gutter={[24, 24]}>
        {rows.map((r) => (
          <Col key={r.id} xs={24} md={12} xl={8} xxl={6}>
            <Card
              className="h-100 exp-card"
              bodyStyle={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", minHeight: 300 }}
            >
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="fw-semibold text-uppercase">{r.nombre}</div>
                <StatusPillAutoridad kind={r.estatus.kind} label={r.estatus.label} />
              </div>

              <div className="d-flex align-items-start gap-2 small">
                <svg className="mt-1" viewBox="0 0 384 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                  {ICONS.pin}
                </svg>
                <div>
                  <div className="text-secondary text-uppercase" style={{ fontSize: 11 }}>Ciudad, Estado</div>
                  <div>{r.ciudad}, {r.estado}</div>
                </div>
              </div>

              <hr className="my-1" />

              <div className="d-flex flex-column gap-1 small text-secondary">
                <div>Tipo: {r.tipo}</div>
              </div>

              <div className="mt-auto">
                <Button
                  type="primary"
                  block
                  onClick={() => canConsultar && navigate(`/autoridades/${r.id}`)}
                  disabled={!canConsultar}
                  title={!canConsultar ? "Sin permiso de consulta" : undefined}
                >
                  <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    {ICONS.info}
                  </svg>
                  Ver detalles
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="mt-3">
        <FooterPagerAutoridad total={total} page={page} setPage={setPage} />
      </div>
    </>
  );
}

export default function AutoridadesPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { perm } = usePermisos() || { perm: () => true };
  const canConsultar = perm("autoridades", "consultar");
  const canInsertar = perm("autoridades", "insertar");

  const autoridadesData = useSelector((state) => state.autoridades?.data);
  const allItems = useMemo(() => {
    if (Array.isArray(autoridadesData)) return autoridadesData;
    if (autoridadesData && Array.isArray(autoridadesData.items)) return autoridadesData.items;
    return [];
  }, [autoridadesData]);

  const [view, setView] = useState("cards");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [page, setPage] = useState(1);

  const lastKeyRef = useRef();

  useEffect(() => {
    if (lastKeyRef.current === "loaded") return;
    lastKeyRef.current = "loaded";
    dispatch(actionAutoridadesAllGet());
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(collapseSpaces(search));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const isActiva = (r) => Number(r.active) === 1;

  const statusCounts = useMemo(
    () => ({
      activas: allItems.filter(isActiva).length,
      inactivas: allItems.filter((r) => !isActiva(r)).length,
    }),
    [allItems]
  );

  const searchFilteredItems = useMemo(() => {
    if (!searchDebounced) return allItems;
    const q = searchDebounced.toLowerCase();
    return allItems.filter((r) => String(r?.nombre || "").toLowerCase().includes(q));
  }, [allItems, searchDebounced]);

  const filteredItems = useMemo(() => {
    if (statusFilter === "activas") return searchFilteredItems.filter(isActiva);
    if (statusFilter === "inactivas") return searchFilteredItems.filter((r) => !isActiva(r));
    return searchFilteredItems;
  }, [searchFilteredItems, statusFilter]);

  const total = filteredItems.length;

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const rows = useMemo(() => paginatedItems.map(mapAutoridadToRow), [paginatedItems]);

  const handleCrear = () => navigate("/autoridades/crear");

  return (
    <ConfigProvider locale={esES}>
      <HappyProvider>
        <main className="trib-main">
          <div className="trib-wrap">
            {/* PANEL FILTROS */}
            <div className="laboral-filters-panel">
              <div className="laboral-panel-header">
                <h1 className="laboral-panel-title">Autoridades · Catálogo</h1>
                <p className="laboral-panel-subtitle">
                  Gestión del catálogo de autoridades (Tribunal, Junta, Conciliación).
                </p>
              </div>

              <div className="laboral-filter-row-2">
                <div className="laboral-field laboral-field--search">
                  <div className="laboral-field-label">BUSCADOR</div>
                  <Input
                    className="laboral-search-modern__input"
                    prefix={<SearchOutlined className="laboral-search-modern__icon" />}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Buscar por nombre..."
                    allowClear
                  />
                </div>

                <div className="laboral-field laboral-field--actions">
                  <div className="laboral-field-label">&nbsp;</div>
                  <div className="laboral-actions">
                    <Button
                      onClick={() => {
                        setSearch("");
                        setStatusFilter("todos");
                        setPage(1);
                      }}
                      className="laboral-btn-clean"
                    >
                      Limpiar
                    </Button>
                    {canInsertar && (
                      <Button
                        type="primary"
                        onClick={handleCrear}
                        className="laboral-btn-create"
                        icon={
                          <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                            {ICONS.plus}
                          </svg>
                        }
                      >
                        Nueva autoridad
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* KPI cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: -12 }}>
              <Col xs={24} sm={12}>
                <KpiCardAutoridad
                  tone="primary"
                  iconSvg={<svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">{ICONS.check}</svg>}
                  value={String(statusCounts.activas)}
                  label="Activas"
                  active={statusFilter === "activas"}
                  onClick={() => { setStatusFilter((p) => (p === "activas" ? "todos" : "activas")); setPage(1); }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <KpiCardAutoridad
                  tone="secondary"
                  iconSvg={<svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">{ICONS.stop}</svg>}
                  value={String(statusCounts.inactivas)}
                  label="Inactivas"
                  active={statusFilter === "inactivas"}
                  onClick={() => { setStatusFilter((p) => (p === "inactivas" ? "todos" : "inactivas")); setPage(1); }}
                />
              </Col>
            </Row>

            {/* LISTADO */}
            <div className="viewWrap">
              <div className="viewWrap__header">
                <div className="viewWrap__title">
                  <h2 className="sectionTitle">Autoridades ({total})</h2>
                  <div className="sectionMeta">{total} encontradas</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <Segmented
                    className="laboral-segmented"
                    value={view}
                    onChange={setView}
                    options={[
                      {
                        value: "cards",
                        label: (
                          <span className="laboral-segmented-option">
                            <svg viewBox="0 0 320 512" width="8" height="13" fill="currentColor" aria-hidden="true">
                              <path d="M88 352C110.1 352 128 369.9 128 392V440C128 462.1 110.1 480 88 480H40C17.91 480 0 462.1 0 440V392C0 369.9 17.91 352 40 352H88zM280 352C302.1 352 320 369.9 320 392V440C320 462.1 302.1 480 280 480H232C209.9 480 192 462.1 192 440V392C192 369.9 209.9 352 232 352H280zM40 320C17.91 320 0 302.1 0 280V232C0 209.9 17.91 192 40 192H88C110.1 192 128 209.9 128 232V280C128 302.1 110.1 320 88 320H40zM280 192C302.1 192 320 209.9 320 232V280C320 302.1 302.1 320 280 320H232C209.9 320 192 302.1 192 280V232C192 209.9 209.9 192 232 192H280zM40 160C17.91 160 0 142.1 0 120V72C0 49.91 17.91 32 40 32H88C110.1 32 128 49.91 128 72V120C128 142.1 110.1 160 88 160H40zM280 32C302.1 32 320 49.91 320 72V120C320 142.1 302.1 160 280 160H232C209.9 160 192 142.1 192 120V72C192 49.91 209.9 32 232 32H280z" />
                            </svg>
                            Tarjetas
                          </span>
                        ),
                      },
                      {
                        value: "table",
                        label: (
                          <span className="laboral-segmented-option">
                            <svg viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                              <path d="M448 32C483.3 32 512 60.65 512 96V416C512 451.3 483.3 480 448 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H448zM224 256V160H64V256H224zM64 320V416H224V320H64zM288 416H448V320H288V416zM448 256V160H288V256H448z" />
                            </svg>
                            Tabla
                          </span>
                        ),
                      },
                    ]}
                  />
                </div>
              </div>

              {view === "table" ? (
                <TableView pageRows={rows} total={total} page={page} setPage={setPage} navigate={navigate} canConsultar={canConsultar} />
              ) : (
                <CardsView rows={rows} total={total} page={page} setPage={setPage} navigate={navigate} canConsultar={canConsultar} />
              )}
            </div>
          </div>
        </main>
      </HappyProvider>
    </ConfigProvider>
  );
}
